import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaStar, FaRegStar, FaStarHalfAlt, FaThumbsUp } from 'react-icons/fa';
import { RiEmotionHappyLine } from 'react-icons/ri';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const NeedHelpPage = () => {
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({
    average_ratings: {
      overall: 0,
      cleanliness: 0,
      value_for_money: 0,
    },
    recommendation_rate: '0%',
  });
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    query_type: 'general',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: '',
  });

  const validateName = (name) => {
    const regex = /^[a-zA-Z\s]*$/;
    if (!name.trim()) return 'Name is required';
    if (!regex.test(name)) return 'Name should contain only letters and spaces';
    if (name.length < 2) return 'Name should be at least 2 characters';
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return 'Email is required';
    if (!regex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateMessage = (message) => {
    if (!message.trim()) return 'Message is required';
    if (message.length < 10) return 'Message should be at least 10 characters';
    return '';
  };

  const validateForm = () => {
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const messageError = validateMessage(formData.message);

    setErrors({
      name: nameError,
      email: emailError,
      message: messageError,
    });

    return !(nameError || emailError || messageError);
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/1`);
        const data = await response.json();
        setPropertyData(data);
      } catch (error) {
        console.error('Error fetching property data:', error);
      }
    };

    const fetchReviews = async () => {
      try {
        setLoadingReviews(true);
        const reviewsResponse = await fetch(`${BQ_BASE_URL}/bq/api/feedback/?min_rating=4`);
        const reviewsData = await reviewsResponse.json();
        setReviews(reviewsData);

        const statsResponse = await fetch(`${BQ_BASE_URL}/bq/api/feedback/stats/ratings`);
        const statsData = await statsResponse.json();

        const safeStats = {
          average_ratings: {
            overall: statsData.average_ratings?.overall || 0,
            cleanliness: statsData.average_ratings?.cleanliness || 0,
            value_for_money: statsData.average_ratings?.value_for_money || 0,
          },
          recommendation_rate: statsData.recommendation_rate || '0%',
        };

        setReviewStats(safeStats);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviewStats({
          average_ratings: {
            overall: 0,
            cleanliness: 0,
            value_for_money: 0,
          },
          recommendation_rate: '0%',
        });
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchPropertyData();
    fetchReviews();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/need-help/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        // Show success toast
        toast.success(
          data.message ||
            'Query submitted successfully! Our support team will reach out to you soon via email.',
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        setFormData({
          name: '',
          email: '',
          query_type: 'general',
          message: '',
        });
        setErrors({
          name: '',
          email: '',
          message: '',
        });
      } else {
        const errorData = await response.json();
        // Show error toast
        toast.error(errorData.message || 'Failed to submit query. Please try again.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Show error toast
      toast.error('An error occurred. Please try again later.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaRegStar key={i} className="text-yellow-400" />);
      }
    }
    return stars;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleSeeAllReviews = () => {
    navigate('/reviews');
  };

  return (
    <div className="min-h-screen bg-white px-4 py-12 sm:px-6 lg:px-8 relative mt-16">
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-8 left-8 text-black hover:text-gray-700 transition font-semibold flex items-center"
      >
        <IoMdArrowRoundBack size={32} className="mr-1" />
      </button>

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-5">
          <h1 className="text-xl font-bold font-poppins text-gray-900 sm:text-4xl">
            How can we help you?
          </h1>
          <p className="mt-1 text-lg font-poppins text-gray-500">
            We're here to assist with any questions or concerns you may have.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 font-poppins">
          {/* Left Column - FAQ and Reviews */}
          <div className="space-y-8">
            {/* Reviews Section */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Guest Reviews</h2>
                {reviewStats && (
                  <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                    <RiEmotionHappyLine className="text-black mr-1" />
                    <span className="text-black font-medium">
                      {reviewStats.recommendation_rate} recommend
                    </span>
                  </div>
                )}
              </div>

              {reviewStats && (
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <div className="text-center p-2 bg-gray-100 rounded-lg">
                    <div className="text-2xl font-bold text-black">
                      {reviewStats.average_ratings.overall.toFixed(1) || '0.0'}
                    </div>
                    <div className="flex justify-center mt-1">
                      {renderStars(reviewStats.average_ratings.overall) || 0}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Overall</p>
                  </div>
                  <div className="text-center p-2 bg-gray-100 rounded-lg">
                    <div className="text-2xl font-bold text-black">
                      {reviewStats.average_ratings.cleanliness.toFixed(1) || '0.0'}
                    </div>
                    <div className="flex justify-center mt-1">
                      {renderStars(reviewStats.average_ratings.cleanliness) || 0}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Cleanliness</p>
                  </div>
                  <div className="text-center p-2 bg-gray-100 rounded-lg">
                    <div className="text-2xl font-bold text-black">
                      {reviewStats.average_ratings.value_for_money.toFixed(1) || '0.0'}
                    </div>
                    <div className="flex justify-center mt-1">
                      {renderStars(reviewStats.average_ratings.value_for_money || 0)}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Value</p>
                  </div>
                </div>
              )}

              {loadingReviews ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading reviews...</p>
                </div>
              ) : reviews.length > 0 ? (
                <div className="space-y-2">
                  {reviews.slice(0, 2).map((review, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-200 pb-6 last:border-0 last:pb-0"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-800 capitalize">{review.name}</h4>
                          <div className="flex items-center mt-1">
                            <div className="flex mr-2">{renderStars(review.overall_rating)}</div>
                            <span className="text-gray-500 text-sm">
                              {formatDate(review.submission_date)}
                            </span>
                          </div>
                        </div>
                        {review.would_recommend && (
                          <div className="flex items-center bg-gray-100 px-2 py-1 rounded text-sm">
                            <FaThumbsUp className="text-black mr-1" />
                            <span className="text-black">Recommends</span>
                          </div>
                        )}
                      </div>
                      {review.message && <p className="text-gray-600 mt-3 ">{review.message}</p>}
                    </div>
                  ))}
                  {reviews.length > 2 && (
                    <button
                      onClick={handleSeeAllReviews}
                      className="mt-6 w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      See All Reviews
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No reviews available yet.</p>
                </div>
              )}
            </div>

            {/* FAQ Section */}
            <div className="bg-white px-4 py-5 rounded-xl shadow-md">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-2">
                {[
                  {
                    question: 'How do I make a reservation?',
                    answer:
                      'You can make a reservation by visiting our reservation website or calling our support team.',
                  },
                  {
                    question: 'What amenities are available at the hotel?',
                    answer:
                      'Our hotel offers free Wi-Fi, a fitness center, swimming pool, spa services and more.',
                  },
                  {
                    question: 'What are the check-in and check-out times?',
                    answer:
                      'Standard check-in is at 11:00 AM and check-out is at 12:00 PM. Early check-in and late check-out may be available upon request.',
                  },
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="border-b border-gray-200 pb-4 last:border-0 last:pb-0"
                  >
                    <h3 className="text-base font-semibold text-gray-800">{faq.question}</h3>
                    <p className="mt-1 text-gray-600 text-base">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form and Info */}
          <div className="space-y-8">
            {/* Contact Form */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Contact Us</h2>
              {/* Removed the submissionStatus div since we're using toasts now */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter you full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-sm "
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-sm "
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="query_type" className="block text-sm font-medium text-gray-700">
                    Query Type
                  </label>
                  <select
                    id="query_type"
                    name="query_type"
                    value={formData.query_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-sm "
                  >
                    <option value="general">General Inquiry</option>
                    <option value="booking">Reservation Issues</option>
                    <option value="payment">Payment Issues</option>
                    <option value="amenities">Amenities</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Enter message"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder:text-sm "
                  ></textarea>
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-black hover:bg-gray-800 "
                  >
                    Submit Query
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information */}
            {propertyData && (
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Our Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 text-base text-gray-500">
                      <p>{propertyData.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 text-base text-gray-500">
                      <p>{propertyData.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 text-base text-gray-500">
                      <p>{propertyData.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Copyright and Terms Section - Added at the bottom */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="text-center text-sm text-gray-600">
            <p className="mb-2">
              © 2026 Rhombus Quest. All rights reserved. Rhombus Quest is a product of Rhombus
              Global Services Inc., USA. Distributed in India by RGS Technologies
            </p>
            <div className="flex justify-center space-x-4 mt-4">
              <a href="/terms" className="text-gray-500 hover:text-gray-900 transition-colors">
                Terms of Service
              </a>
              <span className="text-gray-300">|</span>
              <a href="/privacy" className="text-gray-500 hover:text-gray-900 transition-colors">
                Privacy Policy
              </a>
              <span className="text-gray-300">|</span>
              <a href="/cookies" className="text-gray-500 hover:text-gray-900 transition-colors">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeedHelpPage;
